import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import type { DiscoveryFilters } from "../types/discoveryFilter.types";
import type { DiscoverySearchResult } from "../types/discovery.types";
import { discoveryRepository } from "../repositories/discovery.repository";
import { normalizeDiscoveryFilters } from "../services/discoveryFilter.service";
import {
  validateSearchTokens,
  transformSearchRow,
} from "../services/discoverySearch.service";
import { discoveryKeys } from "../services/discovery.keys";

export function useDiscoveryInfluencers(filters: DiscoveryFilters = {}) {
  const { userCountry } = useAuth();
  const normalized = normalizeDiscoveryFilters(filters);

  // Client-side validation for UX feedback
  const { effectiveSearchTerm, clientWarnings } = validateSearchTokens(
    normalized.searchTerm
  );

  const queryFilters = {
    ...normalized,
    searchTerm: effectiveSearchTerm,
  };

  return useQuery<DiscoverySearchResult>({
    queryKey: discoveryKeys.search(queryFilters, userCountry),
    queryFn: async () => {
      const data = await discoveryRepository.searchInfluencers(
        queryFilters,
        userCountry
      );

      if (!data || data.length === 0) {
        return {
          influencers: [],
          totalCount: 0,
          searchWarnings: clientWarnings,
        };
      }

      const totalCount = data[0]?.total_count || 0;
      const serverWarnings = (data[0]?.search_warnings as string[]) || [];
      const allWarnings = [...clientWarnings, ...serverWarnings];

      const transformed = data.map((inf) =>
        transformSearchRow(inf, userCountry)
      );

      return {
        influencers: transformed,
        totalCount: Number(totalCount),
        searchWarnings: allWarnings,
      };
    },
  });
}
